/**
 * COLESCOMEX Image Library Asset Helper
 *
 * Provides typed access to the approved 24 canonical photographic masters,
 * their metadata, responsive widths, and multi-format paths (AVIF / WebP / JPEG).
 *
 * Conforms to Phase 0 art direction and AGENTS.md asset management rules.
 */

export const CANONICAL_ASSET_IDS = [
  "B01_01",
  "B01_02",
  "B01_03",
  "B01_04",
  "B02_05",
  "B02_06",
  "B02_07",
  "B02_08",
  "B03_09",
  "B03_10",
  "B03_11",
  "B03_12",
  "B04_13",
  "B04_14",
  "B04_15",
  "B04_16",
  "B05_17",
  "B05_18",
  "B05_19",
  "B05_20",
  "B06_21",
  "B06_22",
  "B06_23",
  "B06_24",
] as const;

export type CanonicalAssetId = (typeof CANONICAL_ASSET_IDS)[number];

export const RESPONSIVE_WIDTHS = [640, 960, 1280, 1376] as const;
export type ResponsiveWidth = (typeof RESPONSIVE_WIDTHS)[number];

export type ImageFormat = "avif" | "webp" | "jpg";

export interface AssetMetadata {
  readonly id: CanonicalAssetId;
  readonly canonicalName: string;
  readonly batch: number;
  readonly batchDir: string;
  readonly baseFilename: string;
  readonly semanticRole: string;
  readonly hasWideCrop: boolean;
  readonly nativeWidth: number;
  readonly nativeHeight: number;
  readonly aspectRatio: string;
}

export const ASSET_METADATA_MAP: Record<CanonicalAssetId, AssetMetadata> = {
  B01_01: {
    id: "B01_01",
    canonicalName: "CLIENT_NEED",
    batch: 1,
    batchDir: "batch-01",
    baseFilename: "B01_01_CLIENT_NEED",
    semanticRole: "Client need & executive project definition",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B01_02: {
    id: "B01_02",
    canonicalName: "SPECIFICATION",
    batch: 1,
    batchDir: "batch-01",
    baseFilename: "B01_02_SPECIFICATION",
    semanticRole: "Technical specification & industrial tolerances",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B01_03: {
    id: "B01_03",
    canonicalName: "PROCUREMENT_CONTROL",
    batch: 1,
    batchDir: "batch-01",
    baseFilename: "B01_03_PROCUREMENT_CONTROL",
    semanticRole: "Procurement control & supply agreement audit",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B01_04: {
    id: "B01_04",
    canonicalName: "EVIDENCE",
    batch: 1,
    batchDir: "batch-01",
    baseFilename: "B01_04_EVIDENCE",
    semanticRole: "Metrological evidence & laboratory inspection report",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B02_05: {
    id: "B02_05",
    canonicalName: "SUPPLIER_DISCOVERY",
    batch: 2,
    batchDir: "batch-02",
    baseFilename: "B02_05_SUPPLIER_DISCOVERY",
    semanticRole: "Supplier discovery & automated manufacturing tour",
    hasWideCrop: true,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B02_06: {
    id: "B02_06",
    canonicalName: "FACTORY_AUDIT",
    batch: 2,
    batchDir: "batch-02",
    baseFilename: "B02_06_FACTORY_AUDIT",
    semanticRole: "Factory audit & industrial floor verification",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B02_07: {
    id: "B02_07",
    canonicalName: "SUPPLIER_DUE_DILIGENCE",
    batch: 2,
    batchDir: "batch-02",
    baseFilename: "B02_07_SUPPLIER_DUE_DILIGENCE",
    semanticRole: "Supplier due diligence & ISO certification review",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B02_08: {
    id: "B02_08",
    canonicalName: "SAMPLE_REVIEW",
    batch: 2,
    batchDir: "batch-02",
    baseFilename: "B02_08_SAMPLE_REVIEW",
    semanticRole: "Pre-production sample review & technical evaluation",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B03_09: {
    id: "B03_09",
    canonicalName: "QC_INSPECTION",
    batch: 3,
    batchDir: "batch-03",
    baseFilename: "B03_09_QC_INSPECTION",
    semanticRole: "On-site QC inspection & flashlight weld examination",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B03_10: {
    id: "B03_10",
    canonicalName: "NONCONFORMITY_HOLD",
    batch: 3,
    batchDir: "batch-03",
    baseFilename: "B03_10_NONCONFORMITY_HOLD",
    semanticRole: "Quality hold & non-conformity flaw detection",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B03_11: {
    id: "B03_11",
    canonicalName: "WAREHOUSE_CHINA",
    batch: 3,
    batchDir: "batch-03",
    baseFilename: "B03_11_WAREHOUSE_CHINA",
    semanticRole: "China consolidation warehouse & inventory storage",
    hasWideCrop: true,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B03_12: {
    id: "B03_12",
    canonicalName: "CONSOLIDATION_EXPORT_PACKING",
    batch: 3,
    batchDir: "batch-03",
    baseFilename: "B03_12_CONSOLIDATION_EXPORT_PACKING",
    semanticRole: "Consolidation & industrial export wood crate packing",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B04_13: {
    id: "B04_13",
    canonicalName: "INTL_SHIPMENT_COORDINATION",
    batch: 4,
    batchDir: "batch-04",
    baseFilename: "B04_13_INTL_SHIPMENT_COORDINATION",
    semanticRole: "International ocean freight & logistics telemetry",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B04_14: {
    id: "B04_14",
    canonicalName: "PORT_TERMINAL_OPERATIONS",
    batch: 4,
    batchDir: "batch-04",
    baseFilename: "B04_14_PORT_TERMINAL_OPERATIONS",
    semanticRole: "Deepwater container port terminal & STS cranes",
    hasWideCrop: true,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B04_15: {
    id: "B04_15",
    canonicalName: "CUSTOMS_ARRIVAL_CONTROL",
    batch: 4,
    batchDir: "batch-04",
    baseFilename: "B04_15_CUSTOMS_ARRIVAL_CONTROL",
    semanticRole: "Customs clearance & port arrival physical inspection",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B04_16: {
    id: "B04_16",
    canonicalName: "FINAL_DELIVERY_HANDOFF",
    batch: 4,
    batchDir: "batch-04",
    baseFilename: "B04_16_FINAL_DELIVERY_HANDOFF",
    semanticRole: "Cargo discharge & delivery manifest verification",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B05_17: {
    id: "B05_17",
    canonicalName: "FINAL_B2B_DELIVERY",
    batch: 5,
    batchDir: "batch-05",
    baseFilename: "B05_17_FINAL_B2B_DELIVERY",
    semanticRole: "B2B client delivery & industrial machinery commissioning",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B05_18: {
    id: "B05_18",
    canonicalName: "HOSPITALITY_FFE",
    batch: 5,
    batchDir: "batch-05",
    baseFilename: "B05_18_HOSPITALITY_FFE",
    semanticRole: "Hospitality FF&E project design & material selection",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B05_19: {
    id: "B05_19",
    canonicalName: "ENERGY_ELECTRICAL",
    batch: 5,
    batchDir: "batch-05",
    baseFilename: "B05_19_ENERGY_ELECTRICAL",
    semanticRole: "Medium-voltage electrical infrastructure & switchgear audit",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B05_20: {
    id: "B05_20",
    canonicalName: "CONSTRUCTION_FINISHES",
    batch: 5,
    batchDir: "batch-05",
    baseFilename: "B05_20_CONSTRUCTION_FINISHES",
    semanticRole: "Architectural finishes & natural stone material specification",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B06_21: {
    id: "B06_21",
    canonicalName: "OPERATIONS_VISIBILITY",
    batch: 6,
    batchDir: "batch-06",
    baseFilename: "B06_21_OPERATIONS_VISIBILITY",
    semanticRole: "End-to-end supply chain visibility & curved monitor telemetry",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B06_22: {
    id: "B06_22",
    canonicalName: "EXCEPTION_MANAGEMENT",
    batch: 6,
    batchDir: "batch-06",
    baseFilename: "B06_22_EXCEPTION_MANAGEMENT",
    semanticRole: "Side-by-side component deviation analysis & exception triage",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B06_23: {
    id: "B06_23",
    canonicalName: "EVIDENCE_BASED_DECISION",
    batch: 6,
    batchDir: "batch-06",
    baseFilename: "B06_23_EVIDENCE_BASED_DECISION",
    semanticRole: "Executive analytics workstation & risk dashboard evaluation",
    hasWideCrop: false,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
  B06_24: {
    id: "B06_24",
    canonicalName: "GLOBAL_OPERATIONS_CONTROL",
    batch: 6,
    batchDir: "batch-06",
    baseFilename: "B06_24_GLOBAL_OPERATIONS_CONTROL",
    semanticRole: "Global operations command center (SOC/NOC) multi-screen wall",
    hasWideCrop: true,
    nativeWidth: 1376,
    nativeHeight: 768,
    aspectRatio: "1.79:1",
  },
};

export interface ImageUrlOptions {
  width?: ResponsiveWidth;
  format?: ImageFormat;
  isWide?: boolean;
  basePath?: string;
}

export interface ImageSrcSetOptions {
  format?: ImageFormat;
  isWide?: boolean;
  basePath?: string;
}

const DEFAULT_BASE_PATH = "/assets/04_IMAGES_OPTIMIZED";

/**
 * Returns metadata for a canonical asset ID.
 */
export function getImageMetadata(assetId: CanonicalAssetId): AssetMetadata {
  return ASSET_METADATA_MAP[assetId];
}

/**
 * Type guard for CanonicalAssetId.
 */
export function isCanonicalAssetId(id: string): id is CanonicalAssetId {
  return id in ASSET_METADATA_MAP;
}

/**
 * Generates an absolute or relative URL for a specific image variant.
 */
export function getImageUrl(
  assetId: CanonicalAssetId,
  options: ImageUrlOptions = {}
): string {
  const meta = getImageMetadata(assetId);
  const width = options.width ?? 1376;
  const format = options.format ?? "webp";
  const isWide = Boolean(options.isWide && meta.hasWideCrop);
  const basePath = (options.basePath ?? DEFAULT_BASE_PATH).replace(/\/$/, "");

  const suffix = isWide ? "-wide" : "";
  return `${basePath}/${meta.batchDir}/${meta.baseFilename}${suffix}-${width}w.${format}`;
}

/**
 * Generates a full responsive srcset string for an HTML <img> or <source> element.
 */
export function getImageSrcSet(
  assetId: CanonicalAssetId,
  options: ImageSrcSetOptions = {}
): string {
  const format = options.format ?? "webp";
  const isWide = Boolean(options.isWide);
  const basePath = options.basePath;

  return RESPONSIVE_WIDTHS.map((width) => {
    const url = getImageUrl(assetId, { width, format, isWide, basePath });
    return `${url} ${width}w`;
  }).join(", ");
}

/**
 * Returns all 24 canonical asset IDs.
 */
export function getAllAssetIds(): CanonicalAssetId[] {
  return [...CANONICAL_ASSET_IDS];
}

/**
 * Returns all asset metadata filtered by batch number (1 to 6).
 */
export function getAssetsByBatch(batchNumber: number): AssetMetadata[] {
  return CANONICAL_ASSET_IDS.map((id) => ASSET_METADATA_MAP[id]).filter(
    (item) => item.batch === batchNumber
  );
}
